import type { ReactNode } from "react";

import { ApiError } from "../api/client.ts";
import { Icon, type IconName } from "./Icon.tsx";

/**
 * SPEC 15.3: el UNICO componente que renderiza cargando, error y vacio. Cada
 * pantalla lo usa y ninguna reimplementa esos tres estados.
 *
 * Una pantalla vacia es una invitacion a actuar, no un hueco: el estado vacio
 * SIEMPRE lleva una accion. Y un error explica que paso y como salir de ahi; no
 * pide disculpas y nunca deja al usuario sin siguiente paso.
 */
export function StatusBlock(props: {
  loading?: boolean;
  error?: unknown;
  empty?: boolean;
  emptyTitle?: string;
  emptyText?: string;
  emptyIcon?: IconName;
  emptyAction?: ReactNode;
  onRetry?: (() => void) | undefined;
  children?: ReactNode;
}): ReactNode {
  if (props.loading === true) {
    return <Skeleton />;
  }

  if (props.error !== undefined && props.error !== null) {
    return <ErrorState error={props.error} onRetry={props.onRetry} />;
  }

  if (props.empty === true) {
    return (
      <div className="panel">
        <div className="empty">
          <Icon name={props.emptyIcon ?? "bookmark"} className="empty-ic" />
          <b>{props.emptyTitle ?? "Todavia no hay nada aqui."}</b>
          {props.emptyText !== undefined && <p>{props.emptyText}</p>}
          {props.emptyAction !== undefined && (
            <div className="row" style={{ justifyContent: "center", marginTop: 16 }}>
              {props.emptyAction}
            </div>
          )}
        </div>
      </div>
    );
  }

  return props.children;
}

/**
 * Barras grises con la forma de lo que viene, no una rueda girando: una rueda no
 * dice cuanto falta ni que va a aparecer.
 */
function Skeleton(): ReactNode {
  return (
    <div className="stack" aria-busy="true" aria-label="Cargando">
      <div className="skeleton" style={{ height: 88 }} />
      <div className="skeleton" style={{ height: 140 }} />
      <div className="skeleton" style={{ height: 140 }} />
    </div>
  );
}

function ErrorState(props: { error: unknown; onRetry?: (() => void) | undefined }): ReactNode {
  const api = props.error instanceof ApiError ? props.error : null;

  return (
    <div className="panel">
      <div className="status">
        <b>No se pudo cargar</b>
        <p>{api?.message ?? "Revisa la conexion y vuelve a intentarlo."}</p>
        {props.onRetry !== undefined && (
          <div className="row" style={{ justifyContent: "center", marginTop: 16 }}>
            <button type="button" className="btn ghost" onClick={props.onRetry}>
              <Icon name="refresh" />
              Reintentar
            </button>
          </div>
        )}
        {api?.traceId !== undefined && <p className="trace">{api.traceId}</p>}
      </div>
    </div>
  );
}
